/**
 * Company › People (ADR-022): the owner/admin roster. Management only — a
 * Company role gives no access to any linked workspace's data. Every change
 * to who holds power here is confirmed first.
 *
 * A seat is an INVITATION: adding someone offers them the role, and they
 * hold it only once they accept from their own account. Open invitations
 * are listed, and can be withdrawn, until then.
 */

import { useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/use-toast';
import type { CompanyMemberRole, CompanySettingsDto } from '@/lib/api';
import { useAuth } from '@/auth/hooks/useAuth';
import { UserSearchCombobox } from '@/components/UserSearchCombobox';
import {
  useAddCompanyMember,
  useCancelCompanyMemberInvitation,
  useRemoveCompanyMember,
  useUpdateCompanyMemberRole,
} from '../../hooks/useCompanies';
import { describeError, memberLabel } from './shared';

export function PeopleSection({ data, isOwner }: { data: CompanySettingsDto; isOwner: boolean }) {
  return <RosterCard data={data} isOwner={isOwner} />;
}

type RosterAction =
  | { kind: 'remove'; member: CompanySettingsDto['members'][number] }
  | { kind: 'demote'; member: CompanySettingsDto['members'][number] }
  | { kind: 'promote'; member: CompanySettingsDto['members'][number] };

export function RosterCard({ data, isOwner }: { data: CompanySettingsDto; isOwner: boolean }) {
  const { user } = useAuth();
  const add = useAddCompanyMember(data.company.companyId);
  const changeRole = useUpdateCompanyMemberRole(data.company.companyId);
  const remove = useRemoveCompanyMember(data.company.companyId);
  const cancelInvite = useCancelCompanyMemberInvitation(data.company.companyId);
  const [search, setSearch] = useState('');
  const [username, setUsername] = useState<string | null>(null);
  const [role, setRole] = useState<CompanyMemberRole>('admin');
  // Every act that changes who holds power on the Company is confirmed:
  // removing a seat, and every role change in either direction. Owners can
  // unlink workspaces, take the payer role, add and remove owners and delete
  // the Company, so a wrong click in either direction matters.
  const [pending, setPending] = useState<RosterAction | null>(null);
  const isMe = (m: CompanySettingsDto['members'][number]) =>
    user?.userId !== undefined && m.userId === user.userId;

  async function runPending(): Promise<void> {
    if (!pending) return;
    const { kind, member: m } = pending;
    try {
      if (kind === 'remove') await remove.mutateAsync(m.membershipId);
      else
        await changeRole.mutateAsync({
          membershipId: m.membershipId,
          role: kind === 'promote' ? 'owner' : 'admin',
        });
      setPending(null);
    } catch (err) {
      toast({
        title: kind === 'remove' ? 'Could not remove' : 'Could not change role',
        description: describeError(err),
        variant: 'destructive',
      });
      setPending(null);
    }
  }

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!username) return;
    try {
      await add.mutateAsync({ username, role });
      setSearch('');
      setUsername(null);
      toast({
        title: `Invited @${username}`,
        description: 'They join the Company when they accept the invitation.',
      });
    } catch (err) {
      toast({ title: 'Could not invite', description: describeError(err), variant: 'destructive' });
    }
  }

  async function withdraw(invitationId: string): Promise<void> {
    try {
      await cancelInvite.mutateAsync(invitationId);
      toast({ title: 'Invitation withdrawn' });
    } catch (err) {
      toast({
        title: 'Could not withdraw',
        description: describeError(err),
        variant: 'destructive',
      });
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Company roster</CardTitle>
        <CardDescription>
          Management only. A Company role gives NO access to any linked workspace&apos;s data — a
          workspace&apos;s owner adds people to its roster like anyone else. People you add are
          invited, and join when they accept.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={submit} className="flex flex-wrap items-end gap-2">
          <div className="grid gap-1.5 min-w-[16rem]">
            <Label htmlFor="company-add-user">Invite a person</Label>
            <UserSearchCombobox
              id="company-add-user"
              value={search}
              onValueChange={(v) => {
                setSearch(v);
                setUsername(null);
              }}
              onSelectUser={(u) => setUsername(u.username)}
              placeholder="@username"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="company-add-role">Role</Label>
            <select
              id="company-add-role"
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={role}
              onChange={(e) => setRole(e.target.value as CompanyMemberRole)}
            >
              <option value="admin">Admin</option>
              {isOwner && <option value="owner">Owner</option>}
            </select>
          </div>
          <Button type="submit" disabled={!username || add.isPending}>
            Invite
          </Button>
        </form>

        {data.pendingMembers.length > 0 && (
          <section className="space-y-2">
            <h3 className="-mx-6 flex items-center justify-between border-y border-border bg-muted px-6 py-2 text-sm font-semibold text-foreground">
              <span>Invited — awaiting their answer</span>
              <span className="font-normal text-muted-foreground">
                {data.pendingMembers.length}
              </span>
            </h3>
            <ul className="space-y-1 text-sm">
              {data.pendingMembers.map((inv) => {
                const who =
                  inv.displayName ??
                  (inv.username ? `@${inv.username}` : `…${inv.inviteePrincipalId.slice(-6)}`);
                // Offering an owner seat is an owner's act; so is withdrawing it.
                const canWithdraw = isOwner || inv.role !== 'owner';
                return (
                  <li key={inv.invitationId} className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">
                      <span className="text-foreground">{who}</span> · invited as{' '}
                      <span className="capitalize">{inv.role}</span> · lapses{' '}
                      {new Date(inv.expiresAt).toLocaleDateString()}
                    </span>
                    {canWithdraw && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => void withdraw(inv.invitationId)}
                        disabled={cancelInvite.isPending}
                      >
                        Withdraw
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        <ul className="flex flex-col divide-y divide-border border-y border-border">
          {data.members.map((m) => {
            const label = memberLabel(m);
            const touchesOwner = m.role === 'owner';
            const canEdit = isOwner || !touchesOwner;
            return (
              <li
                key={m.membershipId}
                className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm"
              >
                <span className="truncate text-foreground">
                  {label}{' '}
                  <Badge variant="secondary" className="ml-1 capitalize">
                    {m.role}
                  </Badge>
                </span>
                {canEdit && (
                  <div className="flex items-center gap-2">
                    {isOwner && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          if (m.role !== 'owner') {
                            setPending({ kind: 'promote', member: m });
                            return;
                          }
                          setPending({ kind: 'demote', member: m });
                        }}
                        disabled={changeRole.isPending}
                      >
                        {m.role === 'owner' ? 'Make admin' : 'Make owner'}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPending({ kind: 'remove', member: m })}
                      disabled={remove.isPending}
                    >
                      Remove
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </CardContent>
      <AlertDialog open={pending !== null} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pending?.kind === 'promote'
                ? `Make ${memberLabel(pending.member)} an owner?`
                : pending?.kind === 'demote'
                  ? isMe(pending.member)
                    ? 'Give up your owner role?'
                    : `Make ${memberLabel(pending.member)} an admin?`
                  : pending && isMe(pending.member)
                    ? 'Remove yourself from this Company?'
                    : `Remove ${pending ? memberLabel(pending.member) : ''}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pending?.kind === 'promote'
                ? 'An owner can do everything you can: unlink workspaces, take the payer role, add and remove owners — including you — and delete the Company. Owners cannot expire.'
                : pending?.kind === 'demote' && !isMe(pending.member)
                  ? 'They will no longer be able to unlink workspaces, take the payer role, manage owners or delete the Company. If they are the current payer, another owner must take the payer role first.'
                  : pending?.kind === 'demote'
                    ? 'You will become an admin: you can still manage the roster and approvals and link workspaces, but only an owner can unlink workspaces, switch Trust Fias arches, take the payer role or add and remove owners — including making you an owner again.'
                    : pending && isMe(pending.member)
                      ? 'You will lose your role on this Company and this page. Another owner or admin would have to add you back. Your workspace memberships are not affected.'
                      : 'They will lose their role on this Company. Their workspace memberships are not affected.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending || changeRole.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={remove.isPending || changeRole.isPending}
              onClick={(e) => {
                e.preventDefault();
                void runPending();
              }}
              className={
                pending?.kind === 'remove'
                  ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  : undefined
              }
            >
              {pending?.kind === 'promote'
                ? 'Make owner'
                : pending?.kind === 'demote'
                  ? isMe(pending.member)
                    ? 'Make me an admin'
                    : 'Make admin'
                  : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
